import { Badge, Button, HelpTooltip, LearnMoreLink } from '@auctisync/ui';
import type { Tables, TablesInsert } from '@auctisync/shared';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/Toast';

type Consignor = Tables<'consignors'>;
type TenantOption = Pick<Tables<'tenants'>, 'id' | 'slug' | 'name'>;

type FormState = {
  display_name: string;
  company: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  commission_rate_pct: string; // percent, converted to bps at submit
  tenant_consignor_number: string;
  status: string;
  notes: string;
};

const EMPTY_FORM: FormState = {
  display_name: '',
  company: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: 'US',
  commission_rate_pct: '25.00',
  tenant_consignor_number: '',
  status: 'active',
  notes: '',
};

const STATUSES = ['pending', 'active', 'inactive', 'archived'] as const;

function consignorToForm(c: Consignor): FormState {
  return {
    display_name: c.display_name,
    company: c.company ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    address_line1: c.address_line1 ?? '',
    address_line2: c.address_line2 ?? '',
    city: c.city ?? '',
    region: c.region ?? '',
    postal_code: c.postal_code ?? '',
    country: c.country ?? 'US',
    commission_rate_pct: (c.commission_rate_bps / 100).toFixed(2),
    tenant_consignor_number:
      c.tenant_consignor_number === null ? '' : String(c.tenant_consignor_number),
    status: c.status,
    notes: c.notes ?? '',
  };
}

function formToRow(form: FormState, tenantId: string): TablesInsert<'consignors'> {
  const s = (v: string) => (v.trim() === '' ? null : v.trim());
  const pct = Number(form.commission_rate_pct || '0');
  const bps = Math.round(pct * 100);
  return {
    tenant_id: tenantId,
    display_name: form.display_name.trim(),
    company: s(form.company),
    email: s(form.email),
    phone: s(form.phone),
    address_line1: s(form.address_line1),
    address_line2: s(form.address_line2),
    city: s(form.city),
    region: s(form.region),
    postal_code: s(form.postal_code),
    country: form.country.trim() || 'US',
    commission_rate_bps: bps,
    tenant_consignor_number: form.tenant_consignor_number.trim()
      ? parseInt(form.tenant_consignor_number.trim(), 10)
      : null,
    status: form.status,
    notes: s(form.notes),
  };
}

/**
 * Consignor detail / edit / create. `:id === 'new'` = create mode.
 */
export function AdminConsignorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session } = useSession();
  const claims = readClaims(session);
  const toast = useToast();
  const isCreate = id === 'new';

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [existing, setExisting] = useState<Consignor | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(claims.tenantId ?? '');
  const needsTenantPicker = claims.isSuperAdmin && !claims.tenantId;

  useEffect(() => {
    if (!needsTenantPicker) return;
    void (async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .eq('status', 'active')
        .order('name', { ascending: true });
      const list = data ?? [];
      setTenantOptions(list);
      const first = list[0];
      if (first && !selectedTenantId) setSelectedTenantId(first.id);
    })();
  }, [needsTenantPicker, selectedTenantId]);

  useEffect(() => {
    if (isCreate || !id) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('consignors')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (cancelled) return;
      if (err) setError(err.message);
      else if (!data) setError('Consignor not found.');
      else {
        setExisting(data);
        setForm(consignorToForm(data));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreate]);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.display_name.trim()) {
      setError('Display name is required.');
      return;
    }
    const resolvedTenantId = isCreate
      ? (claims.tenantId ?? selectedTenantId)
      : (existing?.tenant_id ?? claims.tenantId ?? '');
    if (!resolvedTenantId) {
      setError(
        needsTenantPicker
          ? 'Pick a tenant to create this consignor under.'
          : 'Your account has no tenant context.',
      );
      return;
    }
    setSaving(true);
    if (isCreate) {
      const payload = formToRow(form, resolvedTenantId);
      const { data, error: err } = await supabase
        .from('consignors')
        .insert(payload)
        .select('id')
        .single();
      setSaving(false);
      if (err) setError(err.message);
      else void navigate(`/admin/consignors/${data.id}`, { replace: true });
      return;
    }
    if (!id) {
      setSaving(false);
      return;
    }
    const patch = formToRow(form, resolvedTenantId);
    const { error: err } = await supabase.from('consignors').update(patch).eq('id', id);
    setSaving(false);
    if (err) setError(err.message);
    else {
      toast.success('Changes saved.');
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Loading consignor…</div>;
  }

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Link to="/admin/consignors" style={backLinkStyle}>
            ← Consignors
          </Link>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontSize: '1.875rem',
                fontWeight: 700,
                color: 'var(--text)',
                marginBottom: '0.25rem',
              }}
            >
              {isCreate ? 'New consignor' : (existing?.display_name ?? 'Consignor')}
            </h1>
            {existing && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Badge variant={existing.status === 'active' ? 'success' : 'outline'}>
                  {existing.status}
                </Badge>
                {existing.tenant_consignor_number !== null && (
                  <code style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    #{existing.tenant_consignor_number}
                  </code>
                )}
              </div>
            )}
          </div>
          <LearnMoreLink helpSlug="inventory-overview" newTab>
            Inventory + consignor relationship
          </LearnMoreLink>
        </div>
      </header>

      {error && <AlertBox variant="danger">{error}</AlertBox>}

      {needsTenantPicker && isCreate && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-muted, #fef3c7)',
            border: '1px solid var(--admin-hairline, var(--border))',
            fontSize: '0.875rem',
            flexWrap: 'wrap',
          }}
        >
          <Badge variant="outline">Super admin</Badge>
          <span>Tenant:</span>
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            style={selectStyle}
          >
            <option value="">— Pick a tenant —</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        style={{ display: 'grid', gap: '1.5rem' }}
      >
        <Section title="Contact">
          <TwoCol>
            <Field label="Display name" required>
              <input
                type="text"
                required
                value={form.display_name}
                onChange={(e) => setField('display_name', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Company">
              <input
                type="text"
                value={form.company}
                onChange={(e) => setField('company', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Address">
          <TwoCol>
            <Field label="Address line 1">
              <input
                type="text"
                value={form.address_line1}
                onChange={(e) => setField('address_line1', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Address line 2">
              <input
                type="text"
                value={form.address_line2}
                onChange={(e) => setField('address_line2', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="City">
              <input
                type="text"
                value={form.city}
                onChange={(e) => setField('city', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="State / region">
              <input
                type="text"
                value={form.region}
                onChange={(e) => setField('region', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Postal code">
              <input
                type="text"
                value={form.postal_code}
                onChange={(e) => setField('postal_code', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Country">
              <input
                type="text"
                value={form.country}
                onChange={(e) => setField('country', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Commission">
          <TwoCol>
            <Field
              label="Commission rate (%)"
              help={
                <HelpTooltip helpSlug="inventory-overview">
                  Default house commission percentage. Applied to each sale unless overridden per
                  item. Stored as basis points (2500 = 25.00%).
                </HelpTooltip>
              }
            >
              <input
                type="text"
                inputMode="decimal"
                value={form.commission_rate_pct}
                onChange={(e) => setField('commission_rate_pct', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Consignor number (internal)">
              <input
                type="number"
                value={form.tenant_consignor_number}
                onChange={(e) => setField('tenant_consignor_number', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Status">
          <TwoCol>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                style={inputStyle}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '4rem' }}
            />
          </Field>
        </Section>

        {existing && (
          <Section title="Aggregates (maintained by triggers)">
            <div
              style={{
                display: 'flex',
                gap: '1.5rem',
                flexWrap: 'wrap',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
              }}
            >
              <div>
                Items consigned: <strong>{existing.total_items_consigned}</strong>
              </div>
              <div>
                Total sales:{' '}
                <strong>
                  $
                  {(existing.total_sales_cents / 100).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </div>
              <div>
                Total payouts:{' '}
                <strong>
                  $
                  {(existing.total_payouts_cents / 100).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </div>
            </div>
          </Section>
        )}

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingTop: '0.5rem',
            borderTop: '1px solid var(--admin-hairline, var(--border))',
            flexWrap: 'wrap',
          }}
        >
          <Button type="button" variant="outline" asChild>
            <Link to="/admin/consignors">Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : isCreate ? 'Create consignor' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-elev)',
        padding: '1rem 1.25rem',
      }}
    >
      <h2
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: '0.75rem',
        }}
      >
        {title}
      </h2>
      <div style={{ display: 'grid', gap: '0.75rem' }}>{children}</div>
    </section>
  );
}

function TwoCol({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: '0.75rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  help,
  required,
  children,
}: {
  label: string;
  help?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: '0.35rem' }}>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'var(--text)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--semantic-error, #b91c1c)' }}>*</span>}
        {help}
      </span>
      {children}
    </label>
  );
}

function AlertBox({ children }: { variant: 'danger'; children: ReactNode }) {
  const palette = {
    bg: 'var(--surface-danger, #fef2f2)',
    fg: 'var(--text-danger, #991b1b)',
    border: 'var(--border-danger, #fecaca)',
  };
  return (
    <div
      role="alert"
      style={{
        padding: '0.75rem 1rem',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
};

const selectStyle: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

const backLinkStyle: React.CSSProperties = {
  color: 'var(--brand-accent)',
  fontSize: '0.875rem',
  textDecoration: 'none',
};
